import {
  LEGACY_SAMPLE_CLASS_ID,
  LEGACY_SAMPLE_CLASS_NAME,
  isSystemOnboardingClass,
} from "./gettingStartedClass";

export const PRODUCT_TOUR_VERSION = "v1";
export const SAMPLE_CLASS_ID = LEGACY_SAMPLE_CLASS_ID;
export const SAMPLE_CLASS_NAME = LEGACY_SAMPLE_CLASS_NAME;
export const SAMPLE_AGENDA_BOX_ID = "welcomeContentBoxIdOne";
export const SAMPLE_AGENDA_HEADING = "Let's Get Started!";
export const SAMPLE_MULTIBOX_ID = "welcomeContentBoxIdTwo";
export const SAMPLE_TRIAL_COUNTDOWN_INNER_ID = "id0d78dee8ef8d6";
export const SAMPLE_TRIAL_COUNTDOWN_EVENT_NAME = "the End of Your Free Trial";

const COLLAPSED_SAMPLE_AGENDA_HEADING = SAMPLE_AGENDA_HEADING.replace(/\s+/g, "");
export const PRODUCT_TOUR_VIDEO_URL = "https://youtu.be/LS3oSvBQTxM?si=m1x_waNnCKeq15yw";
export const PRODUCT_TOUR_VIDEO_EMBED_URL = "https://www.youtube.com/embed/LS3oSvBQTxM";

export const PRODUCT_TOUR_STATUS = {
  NEW: "new",
  IN_PROGRESS: "in_progress",
  COMPLETED: "completed",
  DISMISSED: "dismissed",
};

export const createDefaultProductTourState = () => ({
  version: PRODUCT_TOUR_VERSION,
  status: PRODUCT_TOUR_STATUS.NEW,
  currentStepId: null,
  startedAt: null,
  completedAt: null,
  dismissedAt: null,
});

const normalizeTourDateValue = (value) => {
  if (value == null) {
    return null;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string") {
    const parsedValue = Date.parse(value);
    return Number.isNaN(parsedValue) ? null : parsedValue;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.getTime();
  }

  if (typeof value.toMillis === "function") {
    return value.toMillis();
  }

  if (typeof value.seconds === "number") {
    const nanoseconds = typeof value.nanoseconds === "number" ? value.nanoseconds : 0;
    return value.seconds * 1000 + Math.floor(nanoseconds / 1000000);
  }

  return null;
};

export const normalizeProductTourDates = (tourState) => {
  if (!tourState || typeof tourState !== "object") {
    return tourState ?? null;
  }

  return {
    ...tourState,
    startedAt: normalizeTourDateValue(tourState.startedAt),
    completedAt: normalizeTourDateValue(tourState.completedAt),
    dismissedAt: normalizeTourDateValue(tourState.dismissedAt),
  };
};

export const toProductTourFirestoreState = (tourState) => ({
  ...tourState,
  startedAt: tourState?.startedAt ? new Date(tourState.startedAt) : null,
  completedAt: tourState?.completedAt ? new Date(tourState.completedAt) : null,
  dismissedAt: tourState?.dismissedAt ? new Date(tourState.dismissedAt) : null,
});

export const normalizeProductTourState = (tourState) => {
  const defaults = createDefaultProductTourState();

  if (!tourState || typeof tourState !== "object") {
    return defaults;
  }

  return {
    ...defaults,
    ...tourState,
    version: tourState.version || defaults.version,
    status: tourState.status || defaults.status,
    startedAt: normalizeTourDateValue(tourState.startedAt),
    completedAt: normalizeTourDateValue(tourState.completedAt),
    dismissedAt: normalizeTourDateValue(tourState.dismissedAt),
  };
};

export const isProductTourComplete = (tourState) =>
  normalizeProductTourState(tourState).status === PRODUCT_TOUR_STATUS.COMPLETED;

export const isProductTourDismissed = (tourState) =>
  normalizeProductTourState(tourState).status === PRODUCT_TOUR_STATUS.DISMISSED;

export const isProductTourPending = (tourState) => {
  const normalizedTour = normalizeProductTourState(tourState);
  return [PRODUCT_TOUR_STATUS.NEW, PRODUCT_TOUR_STATUS.IN_PROGRESS].includes(normalizedTour.status);
};

export const hasUserCreatedClass = (classes = []) =>
  (classes || []).some((classItem) => !isSystemOnboardingClass(classItem));

export const shouldAutoStartProductTour = (user, classes = []) => {
  if (!user) {
    return false;
  }

  if (hasUserCreatedClass(classes)) {
    return false;
  }

  if (user.productTour) {
    return isProductTourPending(user.productTour);
  }

  return user.onboardingIncomplete === true;
};

export const shouldShowNewUserWelcomeModal = (user) => {
  if (!user) {
    return false;
  }

  if (user.productTour) {
    return isProductTourPending(user.productTour);
  }

  return user.onboardingIncomplete === true;
};

export const shouldClearStaleOnboardingAfterClassCreation = (user, classes = []) => {
  if (!user?.uid || !hasUserCreatedClass(classes)) {
    return false;
  }

  return isProductTourPending(user.productTour) || user.onboardingIncomplete === true;
};

export const normalizeSampleAgendaContentBox = (box) => {
  if (!box?.agendaBox || box.contentBoxId !== SAMPLE_AGENDA_BOX_ID) {
    return box;
  }

  const heading = String(box.heading || "");
  const normalizedBox = {
    ...box,
    durationInputMode: "none",
    startTime: null,
  };

  if (heading.replace(/\s+/g, "") === COLLAPSED_SAMPLE_AGENDA_HEADING) {
    return {
      ...normalizedBox,
      heading: SAMPLE_AGENDA_HEADING,
    };
  }

  return normalizedBox;
};

export const formatContentBoxTabLabel = (heading, maxLength = 12) => {
  const normalizedHeading = String(heading || "").trim() === "MultiBox" ? "Box Stack" : heading;
  const text = String(normalizedHeading || "").trim();
  if (!text || text.length <= maxLength) {
    return text;
  }

  const truncated = text.slice(0, maxLength);
  const lastSpaceIndex = truncated.lastIndexOf(" ");
  if (lastSpaceIndex > 0) {
    return `${truncated.slice(0, lastSpaceIndex)}...`;
  }

  return `${truncated}...`;
};

export const validateSampleClassTourEligibility = ({
  user,
  classes = [],
  contentBoxesByClass = {},
}) => {
  if (!user) {
    return {
      isEligible: false,
      reason: "no_user",
    };
  }

  if (user?.settings?.useActiveDates === false) {
    return {
      isEligible: false,
      reason: "active_dates_disabled",
    };
  }

  const sampleClass = classes.find((classEntry) => classEntry?.classId === SAMPLE_CLASS_ID);

  if (!sampleClass) {
    return {
      isEligible: false,
      reason: "sample_class_missing",
    };
  }

  const requiredOrderIds = [SAMPLE_AGENDA_BOX_ID, SAMPLE_MULTIBOX_ID];
  const hasRequiredOrder =
    requiredOrderIds.every((contentBoxId) => sampleClass?.contentBoxesOrder?.includes(contentBoxId));

  if (!hasRequiredOrder) {
    return {
      isEligible: false,
      reason: "sample_class_modified",
    };
  }

  const sampleContentBoxes = contentBoxesByClass?.[SAMPLE_CLASS_ID];

  if (!sampleContentBoxes) {
    return {
      isEligible: true,
      reason: "content_boxes_pending",
      isPending: true,
    };
  }

  const agendaBox = sampleContentBoxes.find(
    (box) => box?.contentBoxId === SAMPLE_AGENDA_BOX_ID
  );
  const multiBox = sampleContentBoxes.find(
    (box) => box?.contentBoxId === SAMPLE_MULTIBOX_ID
  );

  if (!agendaBox?.agendaBox || !multiBox?.multiBox) {
    return {
      isEligible: false,
      reason: "sample_class_modified",
    };
  }

  return {
    isEligible: true,
    reason: "eligible",
    isPending: false,
  };
};
